import ContactsIcon from '@mui/icons-material/Contacts';
import DeleteIcon from '@mui/icons-material/Delete';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NoAccountsIcon from '@mui/icons-material/NoAccounts';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import {
    Box,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Skeleton,
    Stack,
    TableCell,
    TableRow,
    Theme,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import moment from 'moment';
import { useDialog } from 'muibox';
import { enqueueSnackbar } from 'notistack';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import useAxios from '../../../../hooks/useAxios';
import useInventory from '../../../../hooks/useInventory';
import { OnlyFansMessaMessagesQueueDetails, SextforceAutoMassDmMessage } from '../../../../hooks/useMessageUsersMassDmList';
import useSubscriberLists from '../../../../hooks/useSubscriberLists';
import { VaultMediaResponseList } from '../../../../hooks/useSubscriberVaultMedias';
import useSubscribersVaultMediasAdditional from '../../../../hooks/useSubscriberVaultMediasAdditional';
import { SettingsContext } from '../../../../store/SettingsContext';
import { UserContext } from '../../../../store/UserContext';
import { handleHttpError, parseNumberOrString, stripHtmlAndConvertParagraphs } from '../../../../utils/common';
import Bullet from '../../../common/Bullet';
import DeferMountWhenVisible from '../../../common/DeferMountWhenVisible';
import HorizontalScrollList from '../../../common/HorizontalScrollList';
import NA from '../../../common/NA';
import TextInputDialog from '../../../common/TextInputDialog';
import MessageUsersFormEditiorPreview from '../sendMessage/MessageUsersFormEditiorPreview';

type Props = {
    subscriber: any;
    massDm: SextforceAutoMassDmMessage;
    refetchMassDms: () => void;
};

const MassDmMessagePreview = (props: {
    massDm: SextforceAutoMassDmMessage;
    messageDetails: OnlyFansMessaMessagesQueueDetails.Response;
    theme: Theme;
}) => {
    const { massDm, messageDetails, theme } = props;

    const { getManyVaultMediaFromDatabase } = useSubscribersVaultMediasAdditional();

    const [vaultMedias, setVaultMedias] = useState<VaultMediaResponseList[]>([]);
    const combinedMediaIdsKey = useMemo(() => {
        const allMediaIds =
            messageDetails.media && messageDetails.media.length > 0
                ? [...(messageDetails.previews ?? []), ...messageDetails.media.map(media => media.id)]
                : [];
        return allMediaIds.join(',');
    }, [messageDetails]);

    useEffect(() => {
        let isMounted = true;

        const fetchVaultMedias = async () => {
            const mediaIds = combinedMediaIdsKey.split(',').map(id => parseInt(id, 10));
            const medias = await getManyVaultMediaFromDatabase(mediaIds);
            if (isMounted) {
                setVaultMedias(medias);
            }
        };

        fetchVaultMedias();
        return () => {
            isMounted = false;
        };
    }, [combinedMediaIdsKey]);

    return (
        <Box
            sx={{
                '&:hover': {
                    // animate opacity on hover
                    transition: 'opacity 0.3s',
                    opacity: 1,
                },
                // animate opacity on leave
                transition: 'opacity 0.15s',
                opacity: 0.7,
                // smaller font for all children
                '& *': {
                    fontSize: '0.9rem',
                },
            }}
        >
            <MessageUsersFormEditiorPreview
                // editorState={editorState}
                message={messageDetails.text}
                lockMessage={!massDm.isFree}
                lockMessagePrice={messageDetails?.price || 0}
                lockedText={messageDetails?.lockedText || false}
                taggedCreators={
                    messageDetails?.releaseForms?.map(f => {
                        return { id: f.id, name: f.name };
                    }) || []
                }
                taggedReleaseForms={[]}
                mediaFiles={vaultMedias || []}
                mediaFilesPreviews={messageDetails.media && messageDetails.media.length > 0 ? messageDetails.previews : []}
                showMedia
                theme={theme}
                fullWidth
            />
        </Box>
    );
};

const MessageUsersAutoMassDmListTableRow = React.memo((props: Props) => {
    const { subscriber, massDm, refetchMassDms } = props;
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const theme: Theme = useTheme();
    const params = useParams();
    const dialog = useDialog();
    const axios = useAxios();
    const [isDeleting, setIsDeleting] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [openCreateTemplate, setOpenCreateTemplate] = useState(false);

    const messageDetails: OnlyFansMessaMessagesQueueDetails.Response | undefined | null = massDm.messageDetails;

    const {
        lists,
        // data: listsPages,
        isLoading: isLoadingLists,
        hasMore: isListHasMore,
        // hasNextPage,
        // isFetchingNextPage,
        // fetchNextPage,
    } = useSubscriberLists(messageDetails && messageDetails.userLists && messageDetails.userLists.length > 0 ? true : false);

    const { newContentInventoryItem } = useInventory();

    const listNotFound = isListHasMore || isLoadingLists ? <Skeleton width={100} variant="text" /> : 'Unknown List';

    const { data: contentInventory, isLoading: isContentInventoryLoading } = useInventory();

    const templateLabel = useMemo(() => {
        if (!massDm.inventoryId || !massDm.versionId) {
            return null;
        }

        const inventory = contentInventory?.find(entry => entry.inventoryId === massDm.inventoryId);
        const version = inventory?.versions.find(entry => entry.inventorySubId === massDm.versionId);

        if (inventory && version) {
            return `${inventory.name} - ${version.versionName}`;
        }

        return `${massDm.inventoryId}:${massDm.versionId}`;
    }, [contentInventory, massDm.inventoryId, massDm.versionId]);

    const unsendMassDm = async (massDmId: number) => {
        if (!userContext.jwtToken || !settingsContext.apiKey) {
            return;
        }

        setIsDeleting(true);

        const data = await axios
            .delete(`${settingsContext.routes.massDm.base}${params.userId}/list/${massDmId}`)
            .then(async response => {
                return response.data as { success: boolean };
            })
            .catch(error => {
                setIsDeleting(false);
                handleHttpError(error, dialog);

                return { success: false };
            });

        return data;
    };

    const handleCreateTemplate = () => {
        setMenuAnchorEl(null);
        setOpenCreateTemplate(true);
    };

    const handleSubmitCreateTemplate = async (name: string) => {
        setOpenCreateTemplate(false);
        try {
            await newContentInventoryItem(name, 'Variation 1', massDm.messageDetails?.id);
            enqueueSnackbar('Template created successfully', { variant: 'success' });
        } catch {
            enqueueSnackbar('Failed to create template', { variant: 'error' });
        }
    };

    const handleUnsendMassDm = (massDmId: number) => {
        setMenuAnchorEl(null);
        dialog
            .confirm({ message: 'Are you sure you want to unsend this Auto Mass DM?', title: 'Unsend Auto Mass DM' })
            .then(() => {
                unsendMassDm(massDmId).then(data => {
                    setIsDeleting(false);

                    if (data && data.success) {
                        enqueueSnackbar('Auto Mass DM unsent successfully', { variant: 'success' });

                        refetchMassDms();

                        return;
                    }

                    enqueueSnackbar('Failed to unsend Auto Mass DM', { variant: 'error' });
                });
            })
            .catch(() => {});
    };

    return (
        <>
            <TextInputDialog
                open={openCreateTemplate}
                title="Create New Template"
                label="Template name"
                contentText={null}
                submitButtonText="Create"
                cancelButtonText="Cancel"
                initialValue={massDm.name === 'Untitled' ? '' : massDm.name}
                onClose={() => setOpenCreateTemplate(false)}
                onSubmit={handleSubmitCreateTemplate}
            />
            <TableRow
                sx={{
                    '& > *': { borderBottom: 'unset' },
                    // cursor: 'pointer',
                    '& td': {
                        borderBottom: 'unset',
                    },
                }}
            >
                <TableCell sx={{ textWrap: 'nowrap' }}>{moment(massDm.date).format('L LT')}</TableCell>
                <TableCell
                    sx={{
                        // no wrap text
                        textWrap: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {massDm.schedule?.name || massDm.name}
                </TableCell>
                <TableCell align="center" sx={{ textWrap: 'nowrap' }}>
                    {massDm.sentCount?.toLocaleString() || <NA />}
                </TableCell>
                <TableCell align="center" sx={{ textWrap: 'nowrap' }}>
                    {typeof massDm.viewedCount === 'number' ? (
                        <Stack direction="column" alignContent={'center'}>
                            <Typography variant="inherit">{massDm.viewedCount.toLocaleString()}</Typography>
                            <Typography variant="inherit" color="text.secondary">
                                {massDm.viewedCount > 0 ? `${((massDm.viewedCount / massDm.sentCount) * 100).toFixed(2)}%` : '0%'}
                            </Typography>
                        </Stack>
                    ) : (
                        <NA />
                    )}
                </TableCell>
                <TableCell align="center" sx={{ textWrap: 'nowrap' }}>
                    {massDm.purchasedCount && !massDm.isFree ? (
                        <Stack direction="column" alignContent={'center'}>
                            <Typography variant="inherit">{massDm.purchasedCount.toLocaleString()}</Typography>
                            <Typography variant="inherit" color="text.secondary">
                                $
                                {((messageDetails?.price || 0) * massDm.purchasedCount).toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                            </Typography>
                        </Stack>
                    ) : massDm.isFree ? (
                        'FREE'
                    ) : (
                        <NA />
                    )}
                </TableCell>
                <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent={'center'} alignItems={'center'}>
                        <IconButton onClick={e => setMenuAnchorEl(e.currentTarget)} disabled={isDeleting}>
                            <MoreVertIcon />
                        </IconButton>
                        <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={() => setMenuAnchorEl(null)}>
                            <MenuItem onClick={() => handleUnsendMassDm(massDm.massDmId!)}>
                                <ListItemIcon>
                                    <DeleteIcon color="error" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Unsend</ListItemText>
                            </MenuItem>
                            <MenuItem onClick={handleCreateTemplate}>
                                <ListItemText>Use to Create Template</ListItemText>
                            </MenuItem>
                        </Menu>
                    </Stack>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell
                    sx={{ overflow: 'hidden' }}
                    style={{
                        paddingBottom: 4,
                        paddingTop: 4,
                        // ...(!open && { borderBottom: 'none' }),
                    }}
                    colSpan={6}
                >
                    <Stack direction="column" spacing={1}>
                        <Typography variant="inherit" color="textSecondary" component={'div'}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Tooltip title="Send To">
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <ContactsIcon color="primary" sx={{ fontSize: '12pt' }} />
                                    </div>
                                </Tooltip>
                                <HorizontalScrollList>
                                    {messageDetails?.userLists?.map((u, index) => {
                                        const listFound = lists?.find(l => parseNumberOrString(u) === parseNumberOrString(l.id));

                                        return (
                                            <React.Fragment key={index}>
                                                <Typography variant="inherit" noWrap>
                                                    {listFound ? stripHtmlAndConvertParagraphs(listFound?.name) : listNotFound}
                                                </Typography>
                                                {messageDetails.userLists && index < messageDetails.userLists.length - 1 && <Bullet />}
                                            </React.Fragment>
                                        );
                                    })}
                                </HorizontalScrollList>
                            </Stack>
                        </Typography>
                        <Typography variant="inherit" color="textSecondary" component={'div'}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Tooltip title="Exclude">
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <NoAccountsIcon color="error" sx={{ fontSize: '12pt' }} />
                                    </div>
                                </Tooltip>
                                <HorizontalScrollList>
                                    {messageDetails?.excludedLists?.map((u, index) => {
                                        const listFound = lists?.find(l => parseNumberOrString(u) === parseNumberOrString(l.id));
                                        return (
                                            <React.Fragment key={index}>
                                                <Typography variant="inherit">
                                                    {listFound ? stripHtmlAndConvertParagraphs(listFound?.name) : listNotFound}
                                                </Typography>
                                                {messageDetails?.userLists && index < messageDetails.userLists.length - 1 && <Bullet />}
                                            </React.Fragment>
                                        );
                                    })}
                                </HorizontalScrollList>
                            </Stack>
                        </Typography>
                        {(templateLabel || massDm.vaultMediaId) && (
                            <Typography variant="inherit" color="textSecondary" component={'div'}>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                    {templateLabel && (
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Tooltip title="Template used">
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <Inventory2OutlinedIcon color="primary" sx={{ fontSize: '12pt' }} />
                                                </div>
                                            </Tooltip>
                                            {isContentInventoryLoading ? (
                                                <Skeleton width={140} variant="text" />
                                            ) : (
                                                <Typography variant="inherit" noWrap>
                                                    {templateLabel}
                                                </Typography>
                                            )}
                                        </Stack>
                                    )}
                                    {templateLabel && massDm.vaultMediaId && <Bullet />}
                                    {massDm.vaultMediaId && (
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Tooltip title="Album media used">
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <PhotoLibraryOutlinedIcon color="primary" sx={{ fontSize: '12pt' }} />
                                                </div>
                                            </Tooltip>
                                            <Typography variant="inherit" noWrap>
                                                Media #{massDm.vaultMediaId}
                                            </Typography>
                                        </Stack>
                                    )}
                                </Stack>
                            </Typography>
                        )}
                        {messageDetails && messageDetails.text && messageDetails.text.length > 0 && (
                            <DeferMountWhenVisible minHeight={120}>
                                <MassDmMessagePreview massDm={massDm} messageDetails={messageDetails} theme={theme} />
                            </DeferMountWhenVisible>
                        )}
                    </Stack>
                </TableCell>
            </TableRow>
        </>
    );
});

export default MessageUsersAutoMassDmListTableRow;
